// Application configuration — the ONE place the SPA reads its environment
// (CLAUDE.md #6). Dev-vs-prod routing comes from a single env var resolved
// here, never branched on hardcoded hosts scattered through the code.
//
// This is a 100% static SPA: it holds NO secrets (CLAUDE.md #5). The only
// configuration it needs is the base URL of the PMS API it talks to. Vite
// inlines `VITE_`-prefixed vars at build time, so each environment's bundle
// is built with its own VITE_PMS_API_BASE_URL — there is no runtime secret.
//
// The one exception (BRK-S067) is the Google Places browser key: it is baked in
// too, but it is a PUBLIC, browser-exposed key by design (Google's intended
// browser model, secured by HTTP-referrer + API + quota restrictions in the
// Google Cloud console, NOT by hiding it). A referrer-restricted browser key is
// therefore consistent with NN #5 — it is not a secret. It is read HERE, in the
// one config place, from VITE_GOOGLE_PLACES_BROWSER_KEY (NN #6); absent ⇒ the
// address inputs render as today's plain manual form.
//
// The Crisp website id (BRK-S136) is the same kind of value: a PUBLIC
// identifier that ships in the client bundle of every site running Crisp — it
// addresses a workspace, it does not authorize anything. Read HERE from
// VITE_CRISP_WEBSITE_ID (NN #6); absent ⇒ no vendor script is injected at all
// and the Support affordances stay inert.

export interface AppConfig {
  /** Base URL of the PMS API this portal consumes (no trailing slash). */
  pmsApiBaseUrl: string;
  /** Public Google Places (New) browser key, or null when unset (BRK-S067). */
  googlePlacesBrowserKey: string | null;
  /** Public Crisp website id, or null when unset (BRK-S136). */
  crispWebsiteId: string | null;
}

/**
 * Pure config loader — takes an env-like record and returns typed config.
 * Extracted from module-level state so it is exhaustively unit-testable
 * (CLAUDE.md #1) without mutating `import.meta.env`.
 */
export function loadConfig(env: Record<string, string | undefined>): AppConfig {
  const raw = env.VITE_PMS_API_BASE_URL?.trim();
  if (!raw) {
    throw new Error(
      "VITE_PMS_API_BASE_URL is not set. Copy .env.example to .env and set " +
        "the PMS API base URL (see README.md). Each environment's build " +
        "bakes in its own value.",
    );
  }
  // Normalize away a trailing slash so callers can join paths predictably.
  return {
    pmsApiBaseUrl: raw.replace(/\/+$/, ""),
    googlePlacesBrowserKey: resolvePlacesBrowserKey(env),
    crispWebsiteId: resolveCrispWebsiteId(env),
  };
}

/**
 * Pure resolver for the Google Places browser key (BRK-S067) — the ONE place
 * VITE_GOOGLE_PLACES_BROWSER_KEY is read (NN #6). Trimmed; blank/unset ⇒ null,
 * which drives the address inputs to their manual-only form. Independent of the
 * PMS URL so places code can read the key without the app config throwing.
 */
export function resolvePlacesBrowserKey(
  env: Record<string, string | undefined>,
): string | null {
  const raw = env.VITE_GOOGLE_PLACES_BROWSER_KEY?.trim();
  return raw ? raw : null;
}

export function placesBrowserKey(): string | null {
  return resolvePlacesBrowserKey(
    import.meta.env as Record<string, string | undefined>,
  );
}

/**
 * Pure resolver for the Crisp website id (BRK-S136) — the ONE place
 * VITE_CRISP_WEBSITE_ID is read (NN #6). Trimmed; blank/unset ⇒ null, which
 * leaves live chat entirely off: no script injected, no vendor request, and the
 * Support affordances render inert exactly as they did before. That default is
 * deliberate — local dev, Vitest and CI must not depend on a third party, and a
 * dead-looking button beats a button that opens a widget nobody is staffing.
 */
export function resolveCrispWebsiteId(
  env: Record<string, string | undefined>,
): string | null {
  const raw = env.VITE_CRISP_WEBSITE_ID?.trim();
  return raw ? raw : null;
}

export function crispWebsiteId(): string | null {
  return resolveCrispWebsiteId(
    import.meta.env as Record<string, string | undefined>,
  );
}

/**
 * Whether address autocomplete should be active (BRK-S067). Pure so it's
 * unit-tested: true when a browser key is configured (the real path) OR a
 * dev-login offline session is active (the dev-mock path, whose fixture
 * suggestions/details are served by the dev API stub without a key). Absent
 * key + no dev session ⇒ manual-only, the local-dev + real-login default.
 */
export function placesAutocompleteEnabled(
  key: string | null,
  devSession: boolean,
): boolean {
  return Boolean(key) || devSession;
}

/**
 * The resolved config for the running app, read from Vite's `import.meta.env`.
 * Lazily evaluated so importing this module (e.g. in a test that never reads
 * the config) doesn't require the variable to be set.
 */
let cached: AppConfig | undefined;
export function getConfig(): AppConfig {
  cached ??= loadConfig(import.meta.env as Record<string, string | undefined>);
  return cached;
}

/**
 * Whether the dev-only "Dev Login" mock layer is active (BRK-S037).
 *
 * On in two cases, both resolved from build-time env literals so the mock still
 * compiles out of any build that doesn't opt in:
 *   1. `vite dev` runs MODE="development" — always on locally.
 *   2. An explicit `VITE_ENABLE_DEV_LOGIN=true` at build time — for a DEPLOYED
 *      NON-PROD environment (e.g. sandbox1) that wants the skip-auth affordance.
 *
 * `vite build` runs MODE="production" and Vitest runs MODE="test" (both off by
 * MODE); the flag defaults unset. Because Vite inlines both `import.meta.env.MODE`
 * and `import.meta.env.VITE_ENABLE_DEV_LOGIN` as string literals, a build that
 * sets NEITHER folds the guard to `false` and Rollup drops the dev module — so
 * the stub, fixtures, and Dev Login UI still never ship unless a build opts in.
 *
 * ⚠ NEVER set VITE_ENABLE_DEV_LOGIN=true in the PRODUCTION build — that would bake
 * the skip-auth mock into prod. The flag is a sandbox/staging convenience only.
 *
 * Pure (takes an env-like record) so it's unit-testable; `devLoginEnabled()`
 * is the zero-arg reader the app calls.
 */
export function isDevLoginEnabled(env: {
  MODE?: string;
  VITE_ENABLE_DEV_LOGIN?: string;
}): boolean {
  return env.MODE === "development" || env.VITE_ENABLE_DEV_LOGIN === "true";
}

export function devLoginEnabled(): boolean {
  return isDevLoginEnabled(import.meta.env);
}

/** localStorage flag marking an active dev-login (offline) session (BRK-S037).
 *  Owned here — a prod-safe module — so runtime code can key on it WITHOUT
 *  importing the dev layer (which would pull the stub + fixtures into the prod
 *  bundle). `src/dev/*` re-exports it. */
export const DEV_SESSION_KEY = "brk:devSession";

/** Whether a dev-login OFFLINE session is currently active. Runtime state (not
 *  build MODE): a real broker login in dev mode has NO flag and must use the real
 *  backend. Defensive against a missing/blocked localStorage. */
export function hasDevSession(): boolean {
  try {
    return (
      typeof window !== "undefined" &&
      window.localStorage.getItem(DEV_SESSION_KEY) != null
    );
  } catch {
    return false;
  }
}

/**
 * Whether the document dropzone should use the OFFLINE upload SIMULATOR instead
 * of the real direct-to-storage XHR uploader (BRK-S038). True ONLY for an active
 * dev-login offline session — a real broker login in dev mode talks to real PMS
 * and MUST upload for real, or `confirm` truthfully reports the object missing.
 * Gating on build MODE alone was the bug (it simulated for real dev sessions);
 * MODE here only keeps the simulator out of the production bundle. Pure +
 * env-injected so the rule is unit-tested.
 */
export function shouldSimulateUploads(
  env: { MODE?: string },
  devSession: boolean,
): boolean {
  return env.MODE !== "production" && devSession;
}
